import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PdfTool } from "./PdfTool";
import { DxDrawMark } from "./DxDrawMark";
import { trackEvent } from "@/utils/analytics";

const EASE = [0.16, 1, 0.3, 1];
const LINES = ["Deal documents,", "done in", "seconds."];

export const Hero = () => {
  const tryFree = () => {
    trackEvent("cta_click", { source: "hero_primary" });
    const el = document.getElementById("hero-tool-frame");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-azure", "ring-offset-2", "ring-offset-page");
    window.setTimeout(() => el.classList.remove("ring-2", "ring-azure", "ring-offset-2", "ring-offset-page"), 1800);
  };

  return (
    <section id="top" className="relative pt-28 md:pt-36 pb-16 md:pb-24 border-b border-hairline" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16 items-start">
        <div className="lg:col-span-6 lg:pt-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, ease: EASE, delay: 0.15 }}
            className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-8"
          >
            <span className="w-8 h-px bg-bronze inline-block" aria-hidden="true" />
            The document workspace for M&A and legal teams
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.15 }}
            className="mb-7"
          >
            <DxDrawMark size={52} testid="hero-dx-draw" />
          </motion.div>

          <h1 className="font-medium tracking-tight leading-[1.04] text-5xl md:text-6xl lg:text-[64px] mb-8 text-ink" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1 -mb-1">
                <motion.span
                  className="block"
                  initial={{ y: "105%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, ease: EASE, delay: 0.3 + i * 0.12 }}
                >
                  {i === 2 ? (<>{line.slice(0, -1)}<span className="text-azure">.</span></>) : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.85 }}
            className="text-lg text-fog leading-relaxed max-w-lg mb-10"
          >
            Compress, redact, merge and sign — right on this page. Your first conversion is free:
            no signup, no card, nothing stored. One private workspace built for high-stakes deal work.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.95 }}
            className="flex flex-wrap items-center gap-4 mb-12"
            data-testid="hero-conversion-ctas"
          >
            <button type="button" onClick={tryFree} data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 v4-btn-primary h-12 px-7 text-sm font-semibold transition-colors duration-200">
              Try it free — no signup <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" strokeWidth={1.5} />
            </button>
            <a href="#pricing" data-testid="hero-secondary-cta" onClick={() => trackEvent("cta_click", { source: "hero_secondary" })}
              className="inline-flex items-center gap-2 border border-edge rounded-full text-ink h-12 px-7 text-sm font-medium hover:border-ink transition-colors duration-200">
              See pricing
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, ease: EASE, delay: 1.05 }}
            className="flex flex-wrap gap-x-10 gap-y-3 pt-8 border-t border-hairline font-mono text-[11px] uppercase tracking-[0.2em] text-mist"
            data-testid="hero-stats"
          >
            <span><span className="text-ink">16</span> engines</span>
            <span><span className="text-ink">$5/mo</span> entry</span>
            <span><span className="text-ink">Free trial</span> every plan</span>
            <span><span className="text-ink">Zero</span> retention</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.6 }}
          className="lg:col-span-6"
        >
          <div id="hero-tool-frame" className="border border-edge bg-white p-1.5 rounded-sm transition-shadow duration-300" data-testid="hero-tool-frame">
            <PdfTool guest light maxMb={10} />
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-mist mt-3 text-center">
            Try the live tool — your first conversion is free
          </p>
        </motion.div>
      </div>
    </section>
  );
};
